import React, { useState, useEffect, useContext } from "react";
import { useParams, useHistory } from "react-router";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Loader from "../../components/Loader";

import Swal from "sweetalert2";
import Toast from "../../utils/Toast";

import useGetAgent from "../../queries/agent/getAgent";

import CommissionTable from "../../components/CommissionTable";

import createAudit from "../../mutation/audit/createAuditNoHook";
import useCreateAudit from "../../mutation/audit/createAudit";
import getCommission from "../../queries/commission/getCommissionNoHook";
import autoGenerate from "../../utils/autoGenerate";

import { AuthContext } from "../../utils/userProvider";
import {
  FormPageShell,
  FormPageCard,
  FormPageHeader,
  FormActions,
} from "../../components/ListPage";

const schema = yup.object().shape({
  agency_name: yup.string().required("Name is required"),
  agent_code: yup
    .string()
    .required("Agent Code is required")
    .max(14, "Agent Codes have a limit of 14 characters."),
  password: yup
    .string()
    .test(
      "password-edit",
      "Agent Passwords have a minimum of 4 characters and a limit of 8 characters",
      function (value) {
        console.log(value.length);
        if (typeof value != "string") {
          return false;
        }
        if (value.length == 0) {
          return true;
        } else if (value.length > 8) {
          return false;
        } else if (value.length < 4) {
          return false;
        } else {
          return true;
        }
      }
    ),
});

const EditAgent = (props) => {
  const history = useHistory();

  const { id } = useParams();

  const [agentResponse, resetAgent] = useGetAgent(id);
  const [auditResponse, setAuditData, setAuditResponse] = useCreateAudit();
  const [submitDisable, setSubmitDisable] = useState(false);

  const [currentData, setCurrentData] = useState();
  const [snapshotCurrent, setSnapshotCurrent] = useState({});
  const { authState, setAuthState } = useContext(AuthContext);
  const [commissionTable, setCommissionTable] = useState([]);

  const {
    control,
    register: registerAgent,
    handleSubmit: handleAgentSubmit,
    setValue,
    errors: errorsAgent,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const goBack = () => {
    window.location.href = "/dashboard/agent";
  };

  useEffect(() => {
    if (agentResponse.data) {
      console.log(agentResponse.data);
      setSnapshotCurrent(agentResponse.data);
      setCurrentData(agentResponse.data.id);
      setValue("agency_name", agentResponse.data.agency_name);
      setValue("agent_code", agentResponse.data.agent_code);

      setCommissionTable(agentResponse.data.commission_structures);
    }
  }, [agentResponse]);

  useEffect(() => {
    if (errorsAgent) {
      console.log("Errors on Edit");
      console.log(errorsAgent);
    }
  }, [errorsAgent]);

  const checkIfDataChanged = (current, updated) => {
    let items = Object.keys(updated);
    if (updated.password) {
      return true;
    }
    for (let i = 0; i < items.length; i++) {
      if (current[items[i]] && current[items[i]] != updated[items[i]]) {
        return true;
      }
    }
    return false;
  };

  const onAgentSubmit = (values) => {
    let dataObj = {};

    dataObj.agency_name = values.agency_name;
    dataObj.agent_code = values.agent_code;
    dataObj.password = values.password;
    dataObj.commissions = [...commissionTable];

    let agentObj = dataObj;
    delete agentObj.commissions;
    let commissionData = {
      type: "commission-structure",
      agent: currentData,
    };

    console.log(dataObj);

    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          newData: agentObj,
          snapshotCurrent: snapshotCurrent,
          currentData: currentData,
          type: "agent",
          action: "update",
        };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSubmitDisable(true);
        let editedCommissions = false;
        commissionData.actionDescription = result.value.actionDescription;
        let currentCommissions = snapshotCurrent.commission_structures;
        //Handle creates and updates
        if (commissionTable.length > 0) {
          for (let i = 0; i < commissionTable.length; i++) {
            let hasChanged = false;
            if (!commissionTable[i].id) {
              commissionData.newData = commissionTable[i];
              delete commissionData.newData.__v;
              commissionData.action = "create";
              hasChanged = true;

              if (hasChanged) {
                console.log("hasChanged");
                await createAudit(
                  commissionData,
                  authState ? authState.jwt : ""
                );
                editedCommissions = true;
              }
              commissionData = {
                type: "commission-structure",
                agent: currentData,
                actionDescription: result.value.actionDescription,
              };
            } else {
              let currentCommission = currentCommissions.find(
                (x) => x.id == commissionTable[i].id
              );
              if (checkIfDataChanged(currentCommission, commissionTable[i])) {
                commissionData.currentData = commissionTable[i].id;
                commissionData.newData = commissionTable[i];
                delete commissionData.newData.__v;
                let commissionDBObj = await getCommission(
                  { id: currentCommission.id },
                  authState ? authState.jwt : ""
                );
                commissionData.snapshotCurrent = commissionDBObj.data;
                commissionData.action = "update";
                hasChanged = true;
              } else {
                let currentCommission = currentCommissions.find(
                  (x) => x.id == commissionTable[i].id
                );
                if (checkIfDataChanged(currentCommission, commissionTable[i])) {
                  commissionData.currentData = commissionTable[i].id;
                  commissionData.newData = commissionTable[i];
                  let commissionDBObj = await getCommission(
                    { id: currentCommission.id },
                    authState ? authState.jwt : ""
                  );
                  commissionData.snapshotCurrent = commissionDBObj.data;
                  commissionData.action = "update";
                  hasChanged = true;
                }
              }
              if (hasChanged) {
                console.log("hasChanged");
                await createAudit(
                  commissionData,
                  authState ? authState.jwt : ""
                );
                editedCommissions = true;
              }
              commissionData = {
                type: "commission-structure",
                agent: currentData,
                actionDescription: result.value.actionDescription,
              };
            }
            //handle deletes
            commissionData = {
              type: "commission-structure",
              agent: currentData,
              action: "delete",
              actionDescription: result.value.actionDescription,
            };
            for (let i = 0; i < currentCommissions.length; i++) {
              if (
                commissionTable.findIndex(
                  (x) => x.id && x.id == currentCommissions[i].id
                ) == -1
              ) {
                commissionData.currentData = currentCommissions[i].id;
                let commissionDBObj = await getCommission(
                  { id: currentCommissions[i].id },
                  authState ? authState.jwt : ""
                );
                commissionData.snapshotCurrent = commissionDBObj.data;
                await createAudit(
                  commissionData,
                  authState ? authState.jwt : ""
                );
                editedCommissions = true;
              }
            }
            if (checkIfDataChanged(snapshotCurrent, agentObj)) {
              setAuditData(result.value);
            } else if (editedCommissions == true) {
              Toast.fire({
                icon: "success",
                title: "Agent updated",
              });
              history.push("/dashboard/agent");
            }
            setSubmitDisable(false);
          }
        } else {
          setAuditData(result.value);
        }
      }
    });
  };

  useEffect(() => {
    try {
      if (auditResponse.data) {
        Toast.fire({
          icon: "success",
          title: "Agent updated",
        });
        history.push("/dashboard/agent");
      } else if (auditResponse.error) {
        let data = auditResponse.errorMessage.response.data;
        console.log(data);
        if (data) {
          let messageData = data.message;
          let type = messageData.message;

          if (type == "Agent Code Taken") {
            let itemTaken = "<ul>";

            itemTaken += "</ul>";

            Swal.fire({
              icon: "error",
              title: "Error",
              html:
                "<p>Agent Code is already taken by another Agent. Please set another Agent Code.</p>" +
                itemTaken,
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: auditResponse.errorMessage.message,
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: auditResponse.errorMessage.message,
          });
        }
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    }
  }, [auditResponse]);

  const autoGenerateAgentCode = () => {
    setValue("agent_code", autoGenerate(10));
  };
  const autoGeneratePassword = () => {
    setValue("password", autoGenerate(6));
  };

  const renderContent = () => {
    return (
      <FormPageShell>
        <Box
          component="form"
          noValidate
          onSubmit={handleAgentSubmit(onAgentSubmit)}
          sx={{ width: "100%" }}
        >
          <Stack spacing={2}>
            <FormPageCard>
              <FormPageHeader
                title={
                  authState.editAccess
                    ? "Edit Agent"
                    : "Agent"
                }
                onBack={goBack}
                subtitle={
                  authState.editAccess
                    ? "Please fill in the required fields."
                    : undefined
                }
              />
              <TextField
                name="agency_name"
                margin="normal"
                fullWidth
                label="Agency Name*"
                variant="outlined"
                inputRef={registerAgent}
                error={errorsAgent.agency_name != null}
                helperText={
                  errorsAgent.agency_name != null
                    ? errorsAgent.agency_name.message
                    : null
                }
                disabled={
                  submitDisable ||
                  auditResponse.loading ||
                  !authState.editAccess
                }
              />
              <Controller
                as={TextField}
                control={control}
                name="agent_code"
                defaultValue=""
                margin="normal"
                fullWidth
                label="Agent Code*"
                variant="outlined"
                error={errorsAgent.agent_code != null}
                helperText={
                  errorsAgent.agent_code != null
                    ? errorsAgent.agent_code.message
                    : null
                }
                disabled={
                  submitDisable ||
                  auditResponse.loading ||
                  !authState.editAccess
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="auto-generate-password"
                        onClick={autoGenerateAgentCode}
                      >
                        <CachedIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Controller
                as={TextField}
                control={control}
                defaultValue=""
                name="password"
                margin="normal"
                type="text"
                fullWidth
                label="Agent Code Password* (For downloading reports on the YSuites Website)*"
                variant="outlined"
                error={errorsAgent.password != null}
                helperText={
                  errorsAgent.password != null
                    ? errorsAgent.password.message
                    : null
                }
                disabled={
                  submitDisable ||
                  auditResponse.loading ||
                  !authState.editAccess
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="auto-generate-password"
                        onClick={autoGeneratePassword}
                      >
                        <CachedIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormPageCard>
            <FormPageCard>
              <Typography component="h1" variant="h5" fontWeight={700}>
                Agent Commission Structure
              </Typography>
              {authState.editAccess && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Setup the Agent's Commission Structure here.
                </Typography>
              )}
              <CommissionTable
                data={commissionTable}
                setData={setCommissionTable}
              />
            </FormPageCard>
            {authState.editAccess && (
              <FormPageCard>
                <FormActions>
                  <Button
                    type="submit"
                    size="large"
                    variant="contained"
                    color="primary"
                    disabled={
                      submitDisable ||
                      auditResponse.loading ||
                      !authState.editAccess
                    }
                  >
                    Submit
                  </Button>
                </FormActions>
              </FormPageCard>
            )}
          </Stack>
        </Box>
      </FormPageShell>
    );
  };

  return (
    <Box>
      {agentResponse.loading && (
        <FormPageCard>
          <Loader />
        </FormPageCard>
      )}
      {!agentResponse.loading && renderContent()}
    </Box>
  );
};

export default EditAgent;
